import React, { useState, useEffect } from 'react';
import { HStack, VStack, IconButton, Text, Button, Box, Input, Collapsible } from '@chakra-ui/react';
import { CiWarning } from 'react-icons/ci';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

import { patchMyUser, setUserUpdating, updateUser } from '../../actions/actions_users';
import { forgotPassword } from '../../actions/actions_auth';
import Constants from '../../Constants';
import { validatePassword } from '../../utils';
import TwoFactorMethodModal from '../basic/TwoFactorMethod';
import OptConfirmModal from '../basic/OptConfirm';
import { fetchTwoFactorAuthMethods, updateTwoFactorAuthMethod } from '../../services/two-factor-auth';
import DashboardSectionTile from '../core/DashboardSectionTile';
import { Tooltip } from '@/components/ui/tooltip';
import { Field } from '@/components/ui/field';
import { Switch } from '@/components/ui/switch';
import { Alert } from '@/components/ui/alert';
import { getTenantFromDomain } from '../../utils';

// const inputContainerStyleProps = {
//   marginLeft: 'auto !important',
//   width: '264px',
// };
const inputStyleProps = {
  height: '40px',
  backgroundColor: 'white',
  type: 'password',
};

const formFieldSection = {
  width: '400px',
};
const ChangePasswordSchema = Yup.object().shape({
  currentPassword: Yup.string().required('required'),
  newPassword: Yup.string()
    .test('validate-password', function ValidatePass(value) {
      const { path, createError } = this;
      const { email } = this.parent;
      const errorMessage = validatePassword(value, email);
      return errorMessage ? createError({ path, message: errorMessage }) : true;
    })
    .notOneOf([Yup.ref('currentPassword')], 'new password cannot be the same as old password')
    .required('required'),
  passwordConfirmation: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'passwords must match')
    .required('required'),
});

const UserSettings = ({ user, token, password_reset, dispatch }) => {
  const [email, setEmail] = useState('');
  // const [password, setPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showConfirmOtpModal, setShowConfirmOtpModal] = useState(false);

  const [twoFactorAuthMethods, setTwoFactorAuthMethods] = useState([]);
  const [updatedMethod, setUpdatedMethod] = useState(null);

  const isCfcTenant = getTenantFromDomain().includes('cfc');  

  useEffect(() => {
    const getMethods = async () => {
      const userMethods = await fetchTwoFactorAuthMethods(user.token);
      if (userMethods) {
        const methods = Constants.TWO_FACTOR_AUTH_METHOD.map(el => {
          const copyEl = JSON.parse(JSON.stringify(el));
          const obj = userMethods.find(e => e.name === el.value);
          if (obj) {
            if (obj.name === user.default_two_factor_auth_method) {
              copyEl.isDefault = true;
            } else {
              copyEl.isDefault = false;
            }
            copyEl.status = true;
            copyEl.isConfirmed = obj.is_confirmed;
          } else {
            copyEl.status = false;
          }
          return copyEl;
        });
        setTwoFactorAuthMethods(methods);
      }
    };
    getMethods();
  }, []);

  const [showFormsState, setShowFormsState] = useState({
    changePasswordForm: true,
    twoFactorAuthForm: true,
  });

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      passwordConfirmation: '',
    },
    validationSchema: ChangePasswordSchema,
    onSubmit: values => {
      // alert(JSON.stringify(values, null, 2));
      dispatch(setUserUpdating(user));
      const thisUser = { ...user };
      thisUser.current_password = values.currentPassword;
      thisUser.password = values.newPassword;
      const userObjKeys = Object.keys(thisUser);
      userObjKeys.map(objKey => {
        if (!['id', 'password', 'current_password'].includes(objKey)) delete thisUser[objKey];
        return null;
      });
      setSuccess(false);
      dispatch(patchMyUser(thisUser, token));
    },
    validateOnBlur: true,
  });

  useEffect(() => {
    setEmail(user.email);
    if (user.userUISwitched) {
      setSuccess(true);
      const msg = user.next_gen_ui
        ? Constants.USER_SETTINGS_UPDATED_MSG.USER_UI_SWITCH_TO_NEW
        : Constants.USER_SETTINGS_UPDATED_MSG.USER_UI_SWITCH_TO_OLD;
      setSuccessMsg(msg);
    } else if (user.userCredChanged || password_reset) {
      setSuccess(true);
      setSuccessMsg(Constants.USER_SETTINGS_UPDATED_MSG.USER_CRED_CHANGED);
    }
  }, [user, password_reset]);

  const userSetEmail = () => {
    const thisUser = { ...user };
    thisUser.email = email;
    setSuccess(false);
    dispatch(patchMyUser(thisUser, token));
  };

  // const userSetPassword = e => {
  //   const thisUser = { ...user };
  //   thisUser.password = password;
  //   setSuccess(false);
  //   dispatch(patchMyUser(thisUser, token));
  // };

  const userResetPassword = () => {
    dispatch(forgotPassword(user.email));
  };

  const switchToNextGenUi = () => {
    const thisUser = { ...user, next_gen_ui: !user.next_gen_ui, switchUI: true };
    dispatch(patchMyUser(thisUser, token));
  };

  const handleToggleChange = (value, status) => {
    setUpdatedMethod({ value, status });
    setShowPasswordModal(!showPasswordModal);
  };
  const handleTwoFactorAuthModal = (newStatus, isSuccess) => {
    const items = [...twoFactorAuthMethods];
    const index = twoFactorAuthMethods.findIndex(el => el.value === updatedMethod.value);
    items[index].status = newStatus;
    items[index].isConfirmed = false;
    setTwoFactorAuthMethods(items);
    setShowPasswordModal(false);
    if (isSuccess && newStatus) {
      setShowConfirmOtpModal(true);
    }
  };
  const handleConfirmOtp = isSuccess => {
    if (isSuccess) {
      const items = [...twoFactorAuthMethods];
      const index = twoFactorAuthMethods.findIndex(el => el.value === updatedMethod.value);
      items[index].isConfirmed = true;
      setTwoFactorAuthMethods(items);
    }
    setShowConfirmOtpModal(false);
  };

  const showOtpModal = (method, status) => {
    setUpdatedMethod({ value: method, status });
    setShowConfirmOtpModal(true);
  };

  const handleSetDefaultMethod = async method => {
    await updateTwoFactorAuthMethod(user.token, method, true);
    const thisUser = { ...user };
    thisUser.default_two_factor_auth_method = method;
    dispatch(updateUser(thisUser));
  };

  return (
    <>
      {showPasswordModal && (
        <TwoFactorMethodModal
          user={user}
          method={updatedMethod.value}
          handleChangeActiveStatus={handleTwoFactorAuthModal}
          isActive={updatedMethod.status}
        />
      )}
      {showConfirmOtpModal && (
        <OptConfirmModal user={user} method={updatedMethod.value} handleCloseModal={handleConfirmOtp} />
      )}
      <DashboardSectionTile
        title="Change your Password"
        tileStatusComponent={() => (
          <IconButton
            variant="unstyled"
            marginLeft="auto !important"
            fontSize="12px"
            onClick={() =>
              setShowFormsState({ ...showFormsState, changePasswordForm: !showFormsState.changePasswordForm })
            }
          >
            {showFormsState.changePasswordForm ? <FaChevronUp size={16} /> : <FaChevronDown size={16} />}
          </IconButton>
        )}
        paddingRight="18px"
      >
        <Collapsible.Root open={showFormsState.changePasswordForm}>
          <Collapsible.Content>
            <form onSubmit={formik.handleSubmit}>
              <VStack align="stretch" spacing="21px" margin="27px 40px 2px 34px">
                <HStack {...formFieldSection}>
                  <Field
                    label="Old Password:"
                    invalid={formik.errors.currentPassword && formik.touched.currentPassword}
                    errorText={formik.errors.currentPassword}
                  >
                    <Input
                      name="currentPassword"
                      {...inputStyleProps}
                      value={formik.values.currentPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      borderColor={formik.errors.currentPassword && formik.touched.currentPassword && 'red'}
                    />
                  </Field>
                </HStack>
                <HStack {...formFieldSection}>
                  <Field
                    label="New Password:"
                    invalid={formik.errors.newPassword && formik.touched.newPassword}
                    errorText={formik.errors.newPassword}
                  >
                    <Input
                      name="newPassword"
                      {...inputStyleProps}
                      value={formik.values.newPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      borderColor={formik.errors.newPassword && formik.touched.newPassword && 'red'}
                    />
                  </Field>
                </HStack>
                <HStack {...formFieldSection}>
                  <Field
                    label="Confirm Password:"
                    invalid={formik.errors.passwordConfirmation && formik.touched.passwordConfirmation}
                    errorText={formik.errors.passwordConfirmation}
                  >
                    <Input
                      name="passwordConfirmation"
                      {...inputStyleProps}
                      value={formik.values.passwordConfirmation}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      borderColor={formik.errors.passwordConfirmation && formik.touched.passwordConfirmation && 'red'}
                    />
                  </Field>
                </HStack>
                <Button
                  colorScheme="actionSecondary"
                  height="43px"
                  width="179px"
                  fontWeight="medium"
                  fontSize="16px"
                  marginLeft="auto !important"
                  marginTop="30px !important"
                  type="submit"
                  loading={user.updatingUser || false}
                >
                  Update Password
                </Button>
              </VStack>
            </form>
          </Collapsible.Content>
        </Collapsible.Root>
      </DashboardSectionTile>
      <div className="m-grid__item m-grid__item--fluid m-wrapper">
        <div className="m-content">
          <div className="row">
            <div className="col-xl-12">
              <div className="m-portlet m-portlet--full-height ">
                <div className="m-portlet__head">
                  <div className="m-portlet__head-tools">
                    <ul className="nav nav-tabs m-tabs m-tabs-line admin-tabs" role="tablist">
                      <li key="list-menu-user-settings" className="nav-item m-tabs__item active">
                        <Button>User Settings</Button>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="m-portlet__body" style={{ minHeight: '320px' }}>
                  <div className="tab-content">
                    <div className="tab-pane active" id="m_widget2_tab4_content">
                      <div>
                        <div className="row">
                          <div className="col-sm-7">
                            <p>On this screen, you can manage some of your user settings.</p>
                            <Box mt="10px" className="form-group">
                              <label
                                htmlFor="username-form"
                                className="user-settings control-label visible-ie8 visible-ie9"
                              >
                                Email Address
                              </label>
                              <input
                                id="username-form"
                                className="user-settings form-control form-control-solid placeholder-no-fix"
                                type="text"
                                autoComplete="off"
                                placeholder="Username"
                                name="username"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                              />
                              <Button
                                mt="10px"
                                style={{ backgroundColor: isCfcTenant ? '#0F1B48' : '' }}
                                onClick={userSetEmail}
                                className="user-settings btn btn-success email"
                              >
                                Update Email
                              </Button>
                            </Box>
                            <Box mt="10px" className="form-group">
                              <label
                                htmlFor="password-reset-form"
                                className="user-settings control-label visible-ie8 visible-ie9"
                              >
                                Reset Password
                              </label>
                              <br />
                              <Button
                                mt="10px"
                                style={{ backgroundColor: isCfcTenant ? '#0F1B48' : '' }}
                                onClick={userResetPassword}
                                className="user-settings btn btn-success password-reset"
                              >
                                Reset Password
                              </Button>
                              <br />
                              <Button
                                mt="10px"
                                onClick={switchToNextGenUi}
                                style={{ backgroundColor: isCfcTenant ? '#0F1B48' : '' }}
                                className="user-settings btn btn-success password-reset"
                              >
                                {user.next_gen_ui ? 'Switch back to old design' : 'Switch to new design'}
                              </Button>
                            </Box>
                            {success && (
                              <Box mt="20px">
                                <Alert status="success">{successMsg}</Alert>
                              </Box>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default UserSettings;

UserSettings.propTypes = {
  user: PropTypes.shape({
    email: PropTypes.string.isRequired,
    token: PropTypes.string,
    default_two_factor_auth_method: PropTypes.string,
    next_gen_ui: PropTypes.bool,
    userUISwitched: PropTypes.bool,
    userCredChanged: PropTypes.bool,
    updatingUser: PropTypes.bool,
  }).isRequired,
  token: PropTypes.string.isRequired,
  password_reset: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
};
